import {
  Alert,
  Button,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Select,
} from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { isValid } from "iban";

import { useUpdateLandwirtMutation } from "store/masterdata";
import { useLazyGetEnumInfoQuery } from "store/system";
import { UpdateLandwirtParams } from "types/masterdata.types";
import TextArea from "antd/lib/input/TextArea";

interface Props {
  initialValues: {
    kundennummer?: number;
    id?: number;
    firstname?: string;
    lastname?: string;
    email?: string;
    primaryPhone?: string;
    phone2?: string;
    phone3?: string;
    address?: string;
    place?: string;
    postalCode?: string;
    iban?: string;
    ibanName?: string;
    ibanAddress?: string;
    ibanZipCode?: number;
    ibanTown?: string;
    ibanComment?: string;
    ibanOption?: number;
  };
  visible: boolean;
  onClose(): void;
  onSuccess(): void;
}

function UpdateLandwirtForm({
  initialValues,
  visible,
  onClose,
  onSuccess,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const [landwirtForm] = Form.useForm();

  const [updateLandwirt, updateLandwirtResult] = useUpdateLandwirtMutation();
  const [getEnumInfo] = useLazyGetEnumInfoQuery();
  const [ibanOptionValues, setIbanOptionValues] = useState<
    Record<string, number>
  >({});

  const getEnum = async (
    name: string,
    setter: React.Dispatch<React.SetStateAction<Record<string, number>>>
  ) => {
    try {
      const payload = await getEnumInfo({
        enumName: name,
      }).unwrap();
      if (payload) {
        setter(payload);
      }
    } catch (error) {
      console.error("GetEnumInfo Error", error);
    }
  };

  useEffect(() => {
    getEnum("IbanOptionsEnum", setIbanOptionValues);
  }, []);

  const handleLandwirtFormSubmit = (values: UpdateLandwirtParams) => {
    const { kundennummer, ibanZipCode, ...params } = values;
    const cleanIbanZipCode = ibanZipCode
      ? String(ibanZipCode).replace(/[\s\u200B-\u200D\uFEFF]/g, "")
      : undefined;
    updateLandwirt({
      ...params,
      ibanZipCode:
        cleanIbanZipCode?.length === 0 ? undefined : cleanIbanZipCode,
    });
  };

  useEffect(() => {
    if (
      updateLandwirtResult.isSuccess &&
      !updateLandwirtResult.data?.hasErrors
    ) {
      onSuccess();
      onClose();
    }
  }, [updateLandwirtResult]);

  const validateIban = (_: unknown, value: string) => {
    if (!value || isValid(value)) {
      return Promise.resolve();
    }
    return Promise.reject(new Error("Invalid IBAN"));
  };

  return (
    <Modal
      footer={null}
      title={t("masterdata.lw_details.edit_customer_infos")}
      visible={visible}
      onCancel={onClose}
      destroyOnClose
      width={800}
    >
      <Form
        id="landwirt-form"
        form={landwirtForm}
        onFinish={handleLandwirtFormSubmit}
        layout="vertical"
        initialValues={initialValues}
      >
        {updateLandwirtResult.data?.hasErrors && (
          <>
            <Alert
              message={updateLandwirtResult.data?.errorDetails.errorMessage}
              type="error"
            />
            <Divider />
          </>
        )}
        <p>{t("masterdata.lw_details.edit_customer_infos_message")}</p>
        <Divider />
        <Row gutter={20}>
          <Col span={12}>
            <Form.Item name="id" hidden>
              <Input />
            </Form.Item>
            <Form.Item
              label={t("masterdata.lw_details.kundennummer")}
              name="kundennummer"
            >
              <Input disabled />
            </Form.Item>
            <Form.Item
              label={t("masterdata.lw_details.postal_code")}
              name="postalCode"
              rules={[
                {
                  pattern: /^[0-9]+$/,
                  message: t("validation.onlyNumbersAllowed"),
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={t("masterdata.lw_details.lastname")}
              name="lastname"
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={t("masterdata.lw_details.firstname")}
              name="firstname"
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={t("masterdata.lw_details.email")}
              name="email"
              rules={[{ type: "email" }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={`${t("masterdata.lw_details.phone")} 1`}
              name="primaryPhone"
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={`${t("masterdata.lw_details.phone")} 2`}
              name="phone2"
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={`${t("masterdata.lw_details.phone")} 3`}
              name="phone3"
            >
              <Input />
            </Form.Item>

            <Form.Item
              label={t("masterdata.lw_details.adresse")}
              name="address"
            >
              <Input />
            </Form.Item>
            <Form.Item label={t("masterdata.lw_details.ort")} name="place">
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <p>{t("IBAN-Informationen")}</p>
            <Divider />
            <Form.Item
              label={t("masterdata.lw_details.iban")}
              name="iban"
              rules={[{ validator: validateIban }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={t("masterdata.lw_details.ibanName")}
              name="ibanName"
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={t("masterdata.lw_details.ibanAddress")}
              name="ibanAddress"
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={t("masterdata.lw_details.ibanZipCode")}
              name="ibanZipCode"
              rules={[
                {
                  pattern: /^[0-9]+$/,
                  message: t("validation.onlyNumbersAllowed"),
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={t("masterdata.lw_details.ibanTown")}
              name="ibanTown"
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={t("masterdata.lw_details.ibanComment")}
              name="ibanComment"
            >
              <TextArea />
            </Form.Item>
            <Form.Item
              label={t("masterdata.lw_details.ibanOption")}
              name="ibanOption"
            >
              <Select
                options={Object.keys(ibanOptionValues).map((key) => ({
                  label: t(
                    `masterdata.enums.IbanOptionTypeEnum.${ibanOptionValues[key]}`
                  ),
                  value: ibanOptionValues[key],
                }))}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={20}>
          <Col span={12}>
            <Button onClick={onClose} block>
              {t("discard")}
            </Button>
          </Col>
          <Col span={12}>
            <Button
              type="primary"
              htmlType="submit"
              loading={updateLandwirtResult.isLoading}
              block
            >
              {t("save_changes")}
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

export default UpdateLandwirtForm;
